.panel {
  /* Declared on the panel, not the grid — the legend swatches reuse them too.
     `--cell` is the floor (below it the scroller takes over), `--cell-max` the
     ceiling: a year of days stretched edge to edge reads as a slab rather than
     as a calendar, so the cells grow only so far and the chart centres. */
  --cell: 11px;
  --cell-max: 16px;
  --gap: 3px;
  background: var(--surface);
  border-radius: var(--radius);
  padding: 24px;
  min-width: 0;
}

.heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 18px;
}

.headLabel {
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
}

.total {
  font-size: 0.88rem;
  font-variant-numeric: tabular-nums;
  color: var(--muted);
}

.empty {
  font-size: 0.9rem;
  color: var(--muted);
  padding: 8px 0 4px;
}

.stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.stat strong {
  font-family: var(--font-display);
  font-size: 1.6rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: var(--text);
}

.stat em {
  font-style: normal;
  font-size: 0.9rem;
  font-weight: 400;
  color: var(--muted);
}

.stat span {
  font-size: 0.85rem;
  color: var(--muted);
}

/* A year of columns overflows narrow screens; scroll the grid, not the page. */
.scroller {
  overflow-x: auto;
  padding-bottom: 4px;
}

/* Shrink-wrapped and centred: the weekday labels have to stay against the grid,
   so the leftover width is split outside the whole chart rather than inside it. */
.chart {
  display: grid;
  grid-template-columns: 30px auto;
  grid-template-rows: auto auto;
  gap: 4px 6px;
  width: fit-content;
  min-width: min-content;
  margin-inline: auto;
}

/* Same track list as .grid, so a month label stays over the week it names. */
.months {
  grid-column: 2;
  grid-row: 1;
  display: grid;
  grid-template-columns: repeat(var(--weeks), minmax(var(--cell), var(--cell-max)));
  gap: var(--gap);
  font-size: 0.72rem;
  color: var(--muted);
}

/* Labels sit on their own column but the text is wider than one cell, so let it
   spill to the right instead of squeezing the column. */
.months span {
  grid-row: 1;
  white-space: nowrap;
  overflow: visible;
}

/* Seven equal shares of whatever height the cells settle on, so the labels keep
   their rows however wide the panel is. */
.weekdays {
  grid-column: 1;
  grid-row: 2;
  display: grid;
  grid-template-rows: repeat(7, 1fr);
  gap: var(--gap);
  font-size: 0.68rem;
  color: var(--muted);
}

.weekdays span {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* Columns grow with the panel instead of sitting at a fixed 11px, which left
   half of it empty on a desktop. `align-items: start` keeps the row from
   stretching the cell and cancelling its aspect ratio. */
.grid {
  grid-column: 2;
  grid-row: 2;
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(var(--weeks), minmax(var(--cell), var(--cell-max)));
  grid-template-rows: repeat(7, auto);
  align-items: start;
  gap: var(--gap);
}

/* A percentage so the corner keeps its proportion as the cell grows. */
.cell {
  width: var(--cell);
  height: var(--cell);
  border-radius: 18%;
  display: block;
}

/* In the grid the track carries the width and the square carries the height;
   the legend swatches keep the fixed `--cell` box above. */
.grid .cell {
  width: auto;
  height: auto;
  aspect-ratio: 1;
}

/* Padding cells before the first week are not days, so only real ones answer
   the cursor. The ring fits inside the 3px gap, so neighbours stay put. */
.grid .cell:not(.pad) {
  cursor: crosshair;
}

.grid .cell:not(.pad):hover {
  outline: 1px solid var(--text);
  outline-offset: 1px;
}

/* Sequential amber ramp: one hue, dim to bright. */
.pad { background: transparent; }
.l0 { background: var(--surface-2); }
.l1 { background: rgba(217, 126, 36, 0.35); }
.l2 { background: rgba(217, 126, 36, 0.65); }
.l3 { background: var(--amber); }

.legend {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 14px;
  font-size: 0.75rem;
  color: var(--muted);
}

.legend span {
  margin: 0 4px;
}

.tooltip {
  position: fixed;
  z-index: 9999;
  background: var(--canvas);
  border: 1px solid var(--border-md);
  border-radius: 8px;
  padding: 8px 12px;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
  box-shadow: 0 6px 20px rgba(38, 33, 25, 0.18);
}

.tooltipLabel {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--amber-strong);
  white-space: nowrap;
}

.tooltipValue {
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
  color: var(--soft);
  white-space: nowrap;
}

@media (max-width: 620px) {
  .stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px 12px;
  }
}
